// src/pages/Profile.js
import { useState, useEffect } from "react";
import { useAuth } from "../Services/AuthContext";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import "./Profile.scss";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import jsPDF from 'jspdf';
import 'jspdf-autotable'
import Addresses, { getUserAddresses, saveAddress, updateAddress, deleteAddress, setDefaultAddress } from "./Addresses";
import { notifyError } from "../../utils/notify";
import { FormattedMessage } from "react-intl";
import {
    Box, Button, Typography
} from "@mui/material";

class PDFWithAutoTable extends jsPDF {
    autoTable(options) {
        super.autoTable(options);
    }
}

const API_URL = process.env.REACT_APP_API_URL;

const Profile = () => {
    const navigate = useNavigate();
    const { user, logout, setUser } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);

    const [orders, setOrders] = useState([]);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        password: "",
        passwordConfirmation: "",
    });
    const [pwStatus, setPwStatus] = useState("");
    const [addresses, setAddresses] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [invoices, setInvoices] = useState([]);

    const handleLogout = () => {
        logout();
        setUser(null);
        navigate("/login");
    }

    const [view, setView] = useState(() => {
        const savedView = localStorage.getItem('profileView');
        return savedView || "orders";
    });

    // Persist view to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('profileView', view);
    }, [view]);

    // Redirect if not logged in
    useEffect(() => {
        if (!user) {
            // Clear saved view when logging out
            localStorage.removeItem('profileView');
            navigate("/login");
        }
    }, [user, navigate]);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const token = Cookies.get("token");
                const res = await fetch(
                    `${API_URL}/orders?filters[users_permissions_users][id][$eq]=${user.id}&sort=createdAt:desc`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                if (!res.ok) throw new Error("Błąd pobierania zamówień.");
                const data = await res.json();
                setOrders(data.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        if (user?.id) fetchOrders();
    }, [user?.id]);

    // Fetch addresses when profile loads
    const fetchAddresses = async () => {
        try {
            const response = await getUserAddresses(user.id);
            if (response?.data) setAddresses(response.data);
        } catch (err) {
            console.error("Error fetching addresses:", err);
        }
    };

    // Fetch addresses when user ID is available
    useEffect(() => {
        if (user?.id) fetchAddresses();
    }, [user?.id]);

    // Load invoices from localStorage (or you can replace with API fetch)
    useEffect(() => {
        try {
            const saved = localStorage.getItem('invoices');
            if (saved) {
                setInvoices(JSON.parse(saved));
            }
        } catch (err) {
            console.error("Failed to load invoices:", err);
        }
    }, []);

    // Save or update address.
    // Note: the modal (Addresses.js) no longer saves on its own — it hands the
    // validated form data back here so there's a single source of truth for the
    // create/update call and the caller can see (and surface) a failure.
    const handleModalSave = async (addressData) => {
        // isCompany is only used client-side to toggle the NIP/company fields
        const { isCompany, ...payload } = addressData;
        if (editingAddress) {
            await updateAddress(editingAddress.documentId, payload);
        } else {
            await saveAddress(payload, user.id);
        }
        setShowModal(false);
        setEditingAddress(null);
        fetchAddresses();
    };

    // Edit address
    const handleEditAddress = (address) => {
        setEditingAddress(address);
        setShowModal(true);
    };

    // Address routes are keyed by documentId, not the numeric id.
    const handleDeleteAddress = async (documentId) => {
        if (window.confirm("Czy na pewno chcesz usunąć ten adres?")) {
            try {
                await deleteAddress(documentId);
                fetchAddresses();
            } catch (err) {
                console.error("Failed to delete address:", err);
                notifyError(err.message || "Nie udało się usunąć adresu.");
            }
        }
    };

    // The backend unsets the previous default in the same request.
    const handleStarAddress = async (documentId) => {
        try {
            await setDefaultAddress(documentId);
            fetchAddresses();
        } catch (err) {
            console.error("Failed to set default address:", err);
            notifyError(err.message || "Nie udało się ustawić domyślnego adresu.");
        }
    };

    if (!user) return <p>Musisz być zalogowany.</p>;

    // Menu logic
    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    // Handle menu selection
    const handleMenuSelect = (option) => {
        setView(option);
        closeMenu();
    };

    // Handle password change (Strapi: /auth/change-password)
    const handlePasswordChange = async () => {
        try {
            setPwStatus("saving");
            const token = Cookies.get("token");
            const res = await fetch(`${API_URL}/auth/change-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(passwordForm),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error?.message || "Błąd zmiany hasła.");
            setPwStatus("success");
            setPasswordForm({ currentPassword: "", password: "", passwordConfirmation: "" });
        } catch (err) {
            console.error(err);
            setPwStatus("error");
        }
    };

    const handleDownloadPDF = (invoice) => {
        const doc = new PDFWithAutoTable();

        // Set the font size and style
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");

        // Tabular format of the invoice with corresponding information
        const tableData = [
            ["Invoice id", `${invoice.id}`],
            ["Sender's name", `${invoice.name}`],
            ["Sender's email", `${invoice.senderEmail}`],
            ["Recipient's email", `${invoice.recipientEmail}`],
            ["Invoice date", `${invoice.date}`],
            ["Due date", `${invoice.dueDate}`],
            ["Shipping address", `${invoice.shippingAddress}`],
            ["Invoice note", `${invoice.invoiceNote}`],
            ["Invoice description", `${invoice.description}`],
            ["Item quantity", `(${invoice.qty})`],
            ["Rate", `${invoice.rate}`],
            ["Total", `${invoice.total}`],
        ];

        // Customizing the table
        doc.autoTable({
            startY: 40,
            head: [["Item", "Details"]],
            body: tableData,
            headStyles: { fontSize: 18, fontStyle: "bold" },
            styles: { fontSize: 15, fontStyle: "semibold" },
        });

        // To save the PDF with a specific filename. In this case, with the invoice id
        doc.save(`Invoice_${invoice.id}.pdf`);
    };

    return (
        <div className="profile-main">
            <div className="profile-header">
                <h2>Witaj, {user.username}</h2>
                <IconButton onClick={openMenu} aria-controls="profile-menu" aria-haspopup="true">
                    <MoreVertIcon />
                </IconButton>
                <Menu id="profile-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                    <MenuItem onClick={() => handleMenuSelect("orders")}><FormattedMessage id="profile_my_orders" defaultMessage="Moje zamówienia" /></MenuItem>
                    <MenuItem onClick={() => handleMenuSelect("addresses")}><FormattedMessage id="profile_my_addresses" defaultMessage="Moje adresy" /></MenuItem>
                    <MenuItem onClick={() => handleMenuSelect("invoices")}><FormattedMessage id="profile_my_invoices" defaultMessage="Moje faktury" /></MenuItem>
                    <MenuItem onClick={() => handleMenuSelect("settings")}><FormattedMessage id="profile_settings" defaultMessage="Ustawienia" /></MenuItem>
                    <MenuItem onClick={handleLogout}><FormattedMessage id="profile_logout" defaultMessage="Wyloguj" /></MenuItem>
                </Menu>
            </div>

            {/* Views */}
            {view === "orders" && (
                <div className="orders-section">
                    <h3><FormattedMessage id="profile_order_history" defaultMessage="Historia zamówień" /></h3>
                    {orders.length === 0 ? (
                        <p className="empty-state">Brak zamówień.</p>
                    ) : (
                        <div className="orders-list">
                            {orders.map((order) => (
                                <div key={order.id} className="order-card">
                                    <div className="order-top">
                                        <Typography variant="h3" color="textPrimary" >
                                            Zamówienie {order.id}
                                        </Typography>
                                        <Typography variant="body2" color="textPrimary">
                                            {order.orderDate
                                                ? new Date(order.orderDate).toLocaleDateString()
                                                : new Date(order.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </div>
                                    <div className="order-body">
                                        <Typography variant="body2" color="textPrimary">
                                            Status:{" "}
                                            {order.friendlyStatus?.label ??
                                                (order.currentStatus === "pending"
                                                    ? "nie opłacone"
                                                    : order.currentStatus)}
                                        </Typography>
                                        <Typography variant="body2" color="textPrimary">
                                            Kwota: {order.totalAmount} zł
                                        </Typography>
                                        <Typography variant="body2" color="textPrimary">
                                            Produkty: {order.items.map((item, idx) => (
                                                <span key={item.documentId ?? item.id ?? idx}>{item.title} (x{item.quantity}), </span>
                                            ))}</Typography>
                                        {order.shipment && (
                                            <Typography variant="body2" color="textPrimary">
                                                Numer przesyłki: {order.shipment.trackingNumber}{" "}
                                                <a
                                                    href={`https://www.dhl.com/pl-pl/home/tracking/tracking-parcel.html?submit=1&tracking-id=${order.shipment.trackingNumber}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Śledź przesyłkę
                                                </a>
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {view === "addresses" && (
                <div className="addresses-section" style={{ padding: 16 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" color="textPrimary" fontWeight="bold"><FormattedMessage id="profile_addresses_heading" defaultMessage="Adresy" /></Typography>
                        <Button
                            variant="contained"
                            onClick={() => { setEditingAddress(null); setShowModal(true); }}
                            sx={{
                                background: "linear-gradient(90deg, var(--mui-palette-about-gradientStart), var(--mui-palette-about-gradientEnd))",
                                color: "#fff",
                                fontWeight: 500,
                                "&:hover": {
                                    background: "linear-gradient(90deg, var(--mui-palette-about-gradientEnd), var(--mui-palette-about-gradientStart))",
                                    color: "#fff",
                                },
                            }}
                        >
                            + <FormattedMessage id="profile_add_address" defaultMessage="Dodaj adres" />
                        </Button>
                    </Box>

                    <Box>
                        {addresses.length === 0 ? (
                            <p className="empty-state"><FormattedMessage id="profile_no_addresses" defaultMessage="Nie dodano adresów" /></p>
                        ) : (
                            addresses.map((addr) => (
                                <Box
                                    key={addr.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "stretch", sm: "flex-end" },
                                        gap: 2,
                                        mb: 2,
                                        p: { xs: 2, sm: 3 },
                                        border: "1px solid",
                                        borderColor: addr.isDefault ? "text.primary" : "divider",
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
                                            <Typography sx={{ fontFamily: "var(--font-serif)", fontSize: "1.25rem", fontWeight: 400, color: "text.primary" }}>
                                                {addr.firstName} {addr.lastName}
                                            </Typography>
                                            {addr.isDefault && (
                                                <Box component="span" sx={{ fontSize: "0.62rem", letterSpacing: "var(--label-tracking)", textTransform: "uppercase", border: "1px solid", borderColor: "text.primary", px: 1, py: 0.25 }}>
                                                    Domyślny
                                                </Box>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", lineHeight: 1.7 }}>
                                            {addr.company && <>{addr.company}<br /></>}
                                            {addr.address}<br />
                                            {addr.zip} {addr.city}, {addr.country}<br />
                                            Tel: +{String(addr.phone ?? "").replace(/^\+/, "")}
                                            {addr.nip && <><br />NIP: {addr.nip}</>}
                                        </Typography>
                                    </Box>
                                    {/* Quiet text actions in the shop's tracked label style */}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mx: -1, flexShrink: 0, "& .MuiButton-root": { px: 1, py: 0.5 } }}>
                                        <Button size="small" onClick={() => handleEditAddress(addr)}>
                                            <FormattedMessage id="edit" defaultMessage="Edytuj" />
                                        </Button>
                                        {!addr.isDefault && (
                                            <Button size="small" onClick={() => handleStarAddress(addr.documentId)}>
                                                Ustaw domyślny
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            onClick={() => handleDeleteAddress(addr.documentId)}
                                            sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                                        >
                                            <FormattedMessage id="delete" defaultMessage="Usuń" />
                                        </Button>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>

                    <Addresses
                        open={showModal}
                        onClose={() => { setShowModal(false); setEditingAddress(null); }}
                        onSave={handleModalSave}
                        initialData={editingAddress}
                    />
                </div>
            )}

            {view === "invoices" && (
                <div className="invoices-section">
                    <h3><FormattedMessage id="profile_my_invoices" defaultMessage="Moje faktury" /></h3>
                    {invoices.length === 0 ? (
                        <p className="empty-state">Brak zapisanych faktur.</p>
                    ) : (
                        <div className="invoices-list">
                            {invoices.map((invoice, idx) => (
                                <div key={invoice.id ?? invoice.name ?? idx} className="invoice-card">
                                    <strong>{invoice.name}</strong>
                                    <div>{invoice.senderEmail}{invoice.recipientEmail}</div>
                                    <div>{invoice.date} {invoice.dueDate}</div>
                                    <div>{invoice.shippingAddress}</div>
                                    <div>{invoice.invoiceNote}</div>
                                    <div>{invoice.description}</div>
                                    <div>{invoice.qty}</div>
                                    <div>{invoice.rate}</div>
                                    <div>Kwota: {invoice.total} zł</div>
                                    <button onClick={() => handleDownloadPDF(invoice)}>
                                        Download invoice
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {view === "settings" && (
                <div className="settings-section">
                    <h3><FormattedMessage id="profile_settings" defaultMessage="Ustawienia" /></h3>
                    <div className="password-form">
                        <label>
                            Aktualne hasło
                            <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(e) =>
                                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                                }
                            />
                        </label>
                        <label>
                            Nowe hasło
                            <input
                                type="password"
                                value={passwordForm.password}
                                onChange={(e) =>
                                    setPasswordForm({ ...passwordForm, password: e.target.value })
                                }
                            />
                        </label>
                        <label>
                            Powtórz hasło
                            <input
                                type="password"
                                value={passwordForm.passwordConfirmation}
                                onChange={(e) =>
                                    setPasswordForm({ ...passwordForm, passwordConfirmation: e.target.value })
                                }
                            />
                        </label>
                        <button className="save-btn" onClick={handlePasswordChange}>
                            Zmień hasło
                        </button>
                        {pwStatus === "saving" && <p>Zapisywanie…</p>}
                        {pwStatus === "success" && <p className="success">Hasło zmienione 🎉</p>}
                        {pwStatus === "error" && <p className="error">Błąd zmiany hasła ❌</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
